import React from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Droplets,
  Leaf,
  ShieldCheck,
  Sprout,
  Settings,
  BarChart3,
  Thermometer,
  Zap,
  Phone,
} from "lucide-react";

import Navbar from "../layout/Navbar";
import Footer from "../layout/Footer";

import NFTImage from "../image/nft-system.png";
import DWCImage from "../image/dwc-system.png";
import VerticalImage from "../image/vertical-farming.png";
import GreenhouseImage from "../image/greenhouse-solutions.png";


/* =====================================================
   SOLUTION DATA
===================================================== */

const solutions = [
  {
    slug: "nft-system",
    title: "NFT System",
    subtitle: "Nutrient Film Technique",
    image: NFTImage,
    description:
      "A popular and efficient system where a thin film of nutrient solution flows continuously over the roots.",
    features: [
      "Ideal for leafy greens",
      "High oxygen supply",
      "Efficient & scalable",
    ],

    overview:
      "The Nutrient Film Technique (NFT) system continuously circulates a thin film of nutrient-rich water through growing channels. Plant roots receive water, nutrients and oxygen while growing without traditional soil.",

    benefits: [
      {
        icon: <Leaf size={25} />,
        title: "Ideal for Leafy Greens",
        text: "Perfect for lettuce, spinach, herbs and other fast-growing crops.",
      },
      {
        icon: <Droplets size={25} />,
        title: "Efficient Water Use",
        text: "Nutrient solution is continuously circulated through the system.",
      },
      {
        icon: <Zap size={25} />,
        title: "High Oxygen Supply",
        text: "Roots receive excellent oxygen availability for healthy growth.",
      },
      {
        icon: <BarChart3 size={25} />,
        title: "Scalable System",
        text: "Suitable for small farms as well as larger commercial setups.",
      },
    ],
  },

  {
    slug: "dwc-system",
    title: "DWC System",
    subtitle: "Deep Water Culture",
    image: DWCImage,
    description:
      "Plant roots are suspended in nutrient-rich water with continuous aeration for healthy growth.",
    features: [
      "Simple & cost-effective",
      "Great for leafy vegetables",
      "Low maintenance",
    ],

    overview:
      "Deep Water Culture is a simple hydroponic system where plant roots remain suspended in nutrient-rich water. Air pumps provide continuous oxygen to the root zone, supporting healthy plant development.",

    benefits: [
      {
        icon: <Droplets size={25} />,
        title: "Nutrient-Rich Water",
        text: "Plants receive nutrients directly through the water surrounding their roots.",
      },
      {
        icon: <Settings size={25} />,
        title: "Simple Setup",
        text: "A straightforward hydroponic system that is easy to understand and maintain.",
      },
      {
        icon: <Leaf size={25} />,
        title: "Great for Leafy Crops",
        text: "Well suited for lettuce, spinach and other leafy vegetables.",
      },
      {
        icon: <ShieldCheck size={25} />,
        title: "Low Maintenance",
        text: "Simple components make routine monitoring and maintenance easier.",
      },
    ],
  },

  {
    slug: "vertical-farming",
    title: "Vertical Farming",
    subtitle: "Space-Saving Solution",
    image: VerticalImage,
    description:
      "Grow more in less space with vertical hydroponic systems, perfect for urban and indoor farming.",
    features: [
      "Ideal for urban areas",
      "High yield per square foot",
      "Automated options available",
    ],

    overview:
      "Vertical farming uses stacked growing levels to maximize available space. Combined with hydroponic technology, it allows growers to produce crops efficiently in urban, indoor and space-constrained environments.",

    benefits: [
      {
        icon: <Sprout size={25} />,
        title: "Space Efficient",
        text: "Multiple growing levels allow more production within a smaller footprint.",
      },
      {
        icon: <BarChart3 size={25} />,
        title: "Higher Productivity",
        text: "Maximize production potential by utilizing vertical growing space.",
      },
      {
        icon: <Settings size={25} />,
        title: "Automation Ready",
        text: "Systems can be integrated with monitoring and automated controls.",
      },
      {
        icon: <Leaf size={25} />,
        title: "Urban Farming",
        text: "Suitable for cities, indoor farms and areas with limited land.",
      },
    ],
  },

  {
    slug: "greenhouse-solutions",
    title: "Greenhouse Solutions",
    subtitle: "Controlled Environment",
    image: GreenhouseImage,
    description:
      "Custom greenhouse setups with climate control for consistent year-round production.",
    features: [
      "Weather independent",
      "Higher productivity",
      "Suitable for commercial farms",
    ],

    overview:
      "Our greenhouse solutions provide a controlled growing environment where temperature, humidity, irrigation and other growing conditions can be managed according to crop requirements.",

    benefits: [
      {
        icon: <Thermometer size={25} />,
        title: "Climate Control",
        text: "Manage temperature and other environmental conditions for crop growth.",
      },
      {
        icon: <ShieldCheck size={25} />,
        title: "Weather Protection",
        text: "Protect crops from changing outdoor weather conditions.",
      },
      {
        icon: <BarChart3 size={25} />,
        title: "Higher Productivity",
        text: "Controlled growing conditions can support consistent production.",
      },
      {
        icon: <Sprout size={25} />,
        title: "Commercial Farming",
        text: "Suitable for commercial farms and larger controlled-environment projects.",
      },
    ],
  },
];


/* =====================================================
   COMPONENT
===================================================== */

export default function SolutionDetails() {
  const { slug } = useParams();

  const solution = solutions.find(
    (item) => item.slug === slug
  );

  /* ===================================================
     NOT FOUND
  =================================================== */

  if (!solution) {
    return (
      <>
        <Navbar />

        <section className="flex min-h-[60vh] items-center justify-center px-5">
          <div className="text-center">

            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-green-50 text-[#087f4f]">
              <Leaf size={35} />
            </div>

            <h1 className="mt-6 text-4xl font-extrabold text-[#10231c]">
              Solution Not Found
            </h1>

            <p className="mt-3 text-gray-500">
              The solution you are looking for does not exist.
            </p>

            <Link
              to="/solutions"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#087f4f] px-6 py-3 text-sm font-bold text-white"
            >
              <ArrowLeft size={16} />
              Back to Solutions
            </Link>

          </div>
        </section>

        <Footer />
      </>
    );
  }


  return (
    <>

      <main className="bg-white text-[#10231c]">

        {/* =================================================
            BREADCRUMB
        ================================================= */}

        <section className="border-b border-green-100 bg-[#f7fbf8]">

          <div className="mx-auto max-w-[1400px] px-5 py-4 lg:px-10">

            <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">

              <Link
                to="/"
                className="transition hover:text-[#087f4f]"
              >
                Home
              </Link>

              <span>›</span>

              <Link
                to="/solutions"
                className="transition hover:text-[#087f4f]"
              >
                Solutions
              </Link>

              <span>›</span>

              <span className="font-medium text-[#087f4f]">
                {solution.title}
              </span>

            </div>

          </div>

        </section>


        {/* =================================================
            HERO
        ================================================= */}

        <section className="relative overflow-hidden bg-[#f5fbf7]">

          <div className="mx-auto max-w-[1400px] px-5 py-12 lg:px-10 lg:py-16">

            <div className="grid items-center gap-12 lg:grid-cols-2">

              {/* IMAGE */}

              <div className="relative order-2 overflow-hidden rounded-3xl lg:order-1">

                <img
                  src={solution.image}
                  alt={solution.title}
                  className="h-[360px] w-full object-cover sm:h-[480px]"
                />

                <div className="absolute bottom-5 left-5 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-[#087f4f] shadow-lg">
                  Hydroponic Solution
                </div>

              </div>


              {/* CONTENT */}

              <div className="order-1 lg:order-2">

                <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#16854f]">
                  Smart Farming Solution
                </p>

                <h1 className="mt-3 text-4xl font-extrabold leading-[1.05] sm:text-5xl lg:text-[56px]">
                  {solution.title}
                </h1>

                <p className="mt-4 text-xl font-semibold text-[#087f4f]">
                  {solution.subtitle}
                </p>

                <p className="mt-5 max-w-[600px] text-base leading-7 text-gray-600">
                  {solution.description}
                </p>


                {/* FEATURES */}

                <div className="mt-7 space-y-3">

                  {solution.features.map(
                    (feature, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3"
                      >

                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-green-100 text-[#087f4f]">
                          <CheckCircle2 size={16} />
                        </span>

                        <span className="text-sm font-semibold text-gray-700">
                          {feature}
                        </span>

                      </div>
                    )
                  )}

                </div>


                {/* BUTTONS */}

                <div className="mt-8 flex flex-wrap gap-3">

                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 rounded-full bg-[#087f4f] px-7 py-3.5 text-sm font-bold text-white transition hover:bg-[#066b42]"
                  >
                    Get a Custom Quote
                    <ArrowRight size={17} />
                  </Link>

                  <a
                    href="tel:+919876543210"
                    className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-7 py-3.5 text-sm font-bold text-gray-700 transition hover:border-[#087f4f] hover:text-[#087f4f]"
                  >
                    <Phone size={17} />
                    Talk to Experts
                  </a>

                </div>

              </div>

            </div>

          </div>

        </section>


        {/* =================================================
            QUICK STATS
        ================================================= */}

        <section className="border-b border-green-100 bg-white">

          <div className="mx-auto grid max-w-[1400px] grid-cols-2 divide-x divide-green-100 sm:grid-cols-4">

            <QuickStat
              icon={<Leaf size={25} />}
              title="Smart Farming"
              text="Modern technology"
            />

            <QuickStat
              icon={<Droplets size={25} />}
              title="Efficient"
              text="Resource management"
            />

            <QuickStat
              icon={<Sprout size={25} />}
              title="Healthy Crops"
              text="Controlled growth"
            />

            <QuickStat
              icon={<ShieldCheck size={25} />}
              title="Reliable"
              text="Scalable solutions"
            />

          </div>

        </section>


        {/* =================================================
            OVERVIEW
        ================================================= */}

        <section className="py-14 lg:py-16">

          <div className="mx-auto max-w-[1100px] px-5 lg:px-10">

            <div className="text-center">

              <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#16854f]">
                About The Solution
              </p>

              <h2 className="mt-2 text-3xl font-extrabold sm:text-4xl">
                How {solution.title} Works
              </h2>

              <p className="mx-auto mt-5 max-w-[850px] text-base leading-8 text-gray-600">
                {solution.overview}
              </p>

            </div>

          </div>

        </section>


        {/* =================================================
            BENEFITS
        ================================================= */}

        <section className="bg-[#f5fbf7] py-14 lg:py-16">

          <div className="mx-auto max-w-[1400px] px-5 lg:px-10">

            <div className="text-center">

              <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#16854f]">
                Key Benefits
              </p>

              <h2 className="mt-2 text-3xl font-extrabold sm:text-4xl">
                Why Choose {solution.title}?
              </h2>

              <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-gray-600">
                Designed to make modern farming more efficient,
                productive and sustainable.
              </p>

            </div>


            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">

              {solution.benefits.map(
                (benefit, index) => (
                  <div
                    key={index}
                    className="rounded-2xl border border-green-100 bg-white p-6 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-lg"
                  >

                    <div className="flex items-center justify-start gap-3 ">
                      <div className="bg-green-100 text-[#087f4f] rounded-full p-3">

                      {benefit.icon}

                    </div>
                    </div>

                    <h3 className="mt-5 text-lg font-bold">
                      {benefit.title}
                    </h3>

                    <p className="mt-2 text-sm leading-6 text-gray-500">
                      {benefit.text}
                    </p>

                  </div>
                )
              )}

            </div>

          </div>

        </section>


        {/* =================================================
            PROCESS
        ================================================= */}

        <section className="py-14 lg:py-16">

          <div className="mx-auto max-w-[1200px] px-5 lg:px-10">

            <div className="text-center">

              <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#16854f]">
                Our Approach
              </p>

              <h2 className="mt-2 text-3xl font-extrabold sm:text-4xl">
                From Planning to Production
              </h2>

            </div>


            <div className="mt-10 grid gap-5 md:grid-cols-4">

              {[
                {
                  number: "01",
                  title: "Consultation",
                  text: "Understand your farm, crop and production requirements.",
                },
                {
                  number: "02",
                  title: "System Design",
                  text: "Create a solution based on your available space and goals.",
                },
                {
                  number: "03",
                  title: "Installation",
                  text: "Professional setup and integration of the selected system.",
                },
                {
                  number: "04",
                  title: "Support",
                  text: "Ongoing guidance for efficient and reliable operation.",
                },
              ].map((step) => (

                <div
                  key={step.number}
                  className="relative rounded-2xl border border-gray-100 bg-white p-6 shadow-sm"
                >

                  <span className="text-3xl font-extrabold text-[#087f4f]">
                    {step.number}
                  </span>

                  <h3 className="mt-3 font-bold">
                    {step.title}
                  </h3>

                  <p className="mt-2 text-sm leading-6 text-gray-500">
                    {step.text}
                  </p>

                </div>

              ))}

            </div>

          </div>

        </section>


        {/* =================================================
            CTA
        ================================================= */}

        <section className="overflow-hidden bg-[#005536]">

          <div className="mx-auto flex max-w-[1400px] flex-col items-center justify-between gap-6 px-5 py-12 md:flex-row lg:px-10">

            <div>

              <p className="text-sm font-bold uppercase tracking-[0.16em] text-green-300">
                Ready to Build?
              </p>

              <h2 className="mt-2 text-3xl font-extrabold text-white">
                Build Your {solution.title} With Us
              </h2>

              <p className="mt-2 max-w-xl text-sm leading-6 text-white/70">
                Talk to our experts about your farming requirements
                and get a customized solution for your project.
              </p>

            </div>


            <Link
              to="/contact"
              className="inline-flex flex-shrink-0 items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-bold text-[#087f4f] transition hover:bg-green-50"
            >
              Get Free Consultation
              <ArrowRight size={17} />
            </Link>

          </div>

        </section>


        {/* =================================================
            OTHER SOLUTIONS
        ================================================= */}

        <section className="bg-[#f7fbf8] py-14">

          <div className="mx-auto max-w-[1400px] px-5 lg:px-10">

            <div className="flex items-end justify-between">

              <div>

                <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#16854f]">
                  Explore More
                </p>

                <h2 className="mt-2 text-3xl font-extrabold">
                  Other Solutions
                </h2>

              </div>

              <Link
                to="/solutions"
                className="hidden items-center gap-2 text-sm font-bold text-[#087f4f] sm:flex"
              >
                View All
                <ArrowRight size={16} />
              </Link>

            </div>


            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">

              {solutions
                .filter(
                  (item) => item.slug !== solution.slug
                )
                .map((item) => (

                  <Link
                    key={item.slug}
                    to={`/solutions/${item.slug}`}
                    className="group overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-lg"
                  >

                    <div className="overflow-hidden">

                      <img
                        src={item.image}
                        alt={item.title}
                        className="h-48 w-full object-cover transition duration-500 group-hover:scale-105"
                      />

                    </div>

                    <div className="p-5">

                      <p className="text-xs font-bold uppercase text-[#16854f]">
                        {item.subtitle}
                      </p>

                      <h3 className="mt-1 text-lg font-bold">
                        {item.title}
                      </h3>

                      <p className="mt-2 line-clamp-2 text-sm leading-6 text-gray-500">
                        {item.description}
                      </p>

                      <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[#087f4f]">
                        Learn More
                        <ArrowRight size={15} />
                      </span>

                    </div>

                  </Link>

                ))}

            </div>

          </div>

        </section>

      </main>

    </>
  );
}


/* =====================================================
   QUICK STAT
===================================================== */

function QuickStat({
  icon,
  title,
  text,
}) {
  return (
    <div className="flex items-center gap-3 px-5 py-6 sm:px-8">

      <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-green-100 text-[#087f4f]">
        {icon}
      </div>

      <div>

        <h3 className="text-sm font-bold">
          {title}
        </h3>

        <p className="mt-1 text-xs text-gray-500">
          {text}
        </p>

      </div>

    </div>
  );
}